/**
 * The modals that edit a record.
 *
 * An entry here has the same shape as one in `modalFormData` — `titleBar`,
 * `elements`, `buttons`, `submit` — because since task 33.1 tier 3 it is
 * rendered by the same component. What makes it a card modal is two declared
 * things and nothing else: its single element is a `cardSection`, and its
 * `submit` goes `to: 'entity'` instead of back into the caller's store.
 *
 * It stays a separate map from `modalFormData` because it is addressed
 * differently: these are keyed by **entity**, and a connect field asks
 * `modalCardData[entity]` to decide whether it offers an edit pencil at all.
 * `modalFormData` is keyed by form name. Merging them would make a typo in
 * either resolve to something in the other.
 *
 * `path` and `queryArray` are the record's endpoint and its query key, so they
 * sit on the entry rather than inside `submit` — `submit` says where the answer
 * goes, not how the entity is addressed. Whether the modal loads first is the
 * caller's business (a connect field's pencil loads; kanban's "+" does not), so
 * it is a prop, not config.
 */
export const modalCardData = {
    campaign: {
        id: 'campaign',
        titleBar: { new: 'campaign.modal.new.title', edit: 'campaign.modal.edit.title' },
        path: 'campaign',
        queryArray: ['campaign'],
        submit: { to: 'entity' },
        elements: [{ type: 'cardSection', sectionRef: { view: 'campaignCard' } }],
    },
    contact: {
        id: 'contact',
        titleBar: { new: 'contact.modal.new.title', edit: 'contact.modal.edit.title' },
        path: 'contact',
        queryArray: ['contact'],
        submit: { to: 'entity' },
        elements: [{ type: 'cardSection', sectionRef: { view: 'contactCard' } }],
    },
    contactPerson: {
        id: 'contactPerson',
        titleBar: { new: 'contact.person.modal.new.title', edit: 'contact.person.modal.edit.title' },
        path: 'contact/:id/contactPerson',
        queryArray: ['contactPerson', ':id'],
        submit: { to: 'entity' },
        elements: [{ type: 'cardSection', sectionRef: { view: 'contactPersonCard' } }],
    },
    customerGroup: {
        id: 'customerGroup',
        titleBar: { new: 'customerGroup.modal.new.title', edit: 'customerGroup.modal.edit.title' },
        path: 'customerGroup',
        queryArray: ['customerGroup'],
        submit: { to: 'entity' },
        elements: [{ type: 'cardSection', sectionRef: { view: 'customerGroupCard' } }],
    },
    priceList: {
        id: 'priceList',
        titleBar: { new: 'priceList.modal.new.title', edit: 'priceList.modal.edit.title' },
        path: 'priceList',
        queryArray: ['priceList'],
        submit: { to: 'entity' },
        elements: [{ type: 'cardSection', sectionRef: { view: 'priceListCard' } }],
    },
    product: {
        id: 'product',
        titleBar: { new: 'product.modal.new.title', edit: 'product.modal.edit.title' },
        path: 'product',
        queryArray: ['product'],
        submit: { to: 'entity' },
        elements: [{ type: 'cardSection', sectionRef: { view: 'productCard' } }],
    },
    productGroup: {
        id: 'productGroup',
        titleBar: { new: 'productGroup.modal.new.title', edit: 'productGroup.modal.edit.title' },
        path: 'productGroup',
        queryArray: ['productGroup'],
        submit: { to: 'entity' },
        elements: [{ type: 'cardSection', sectionRef: { view: 'productGroupCard' } }],
    },
    projectTask: {
        id: 'projectTask',
        titleBar: { new: 'project.task.modal.new.title', edit: 'project.task.modal.edit.title' },
        path: 'project/:id/projectTask',
        queryArray: ['list', 'project/:id/projectTask'],
        loadSettings: ['projectSettings'],
        submit: { to: 'entity' },
        elements: [{ type: 'cardSection', sectionRef: { view: 'projectTaskCard' } }],
    },
    warehouse: {
        id: 'warehouse',
        titleBar: { new: 'warehouse.modal.new.title', edit: 'warehouse.modal.edit.title' },
        path: 'warehouse',
        queryArray: ['warehouse'],
        submit: { to: 'entity' },
        elements: [{ type: 'cardSection', sectionRef: { view: 'warehouseCard' } }],
    },
    workCapability: {
        id: 'workCapability',
        titleBar: { new: 'workCapability.modal.new.title', edit: 'workCapability.modal.edit.title' },
        path: 'workCapability',
        queryArray: ['workCapability'],
        submit: { to: 'entity' },
        elements: [{ type: 'cardSection', sectionRef: { view: 'workCapabilityCard' } }],
    },
    workOperationConsumption: {
        id: 'workOperationConsumption',
        titleBar: { new: 'workExecution.modal.consumption.title', edit: 'workExecution.modal.consumption.title' },
        path: 'workOperation/:id/workOperationConsumption',
        queryArray: ['workOperationConsumption', ':id'],
        submit: { to: 'entity' },
        elements: [{ type: 'cardSection', sectionRef: { view: 'workOperationConsumptionCard' } }],
        buttons: [{ id: 'save', action: 'submit', label: 'all.form.button.save' }],
    },
    workOperationOutput: {
        id: 'workOperationOutput',
        titleBar: { new: 'workExecution.modal.output.title', edit: 'workExecution.modal.output.title' },
        path: 'workOperation/:id/workOperationOutput',
        queryArray: ['workOperationOutput', ':id'],
        submit: { to: 'entity' },
        elements: [{ type: 'cardSection', sectionRef: { view: 'workOperationOutputCard' } }],
        buttons: [{ id: 'save', action: 'submit', label: 'all.form.button.save' }],
    },
}
